import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Tab from '@mui/material/Tab';
import Typography from "@mui/material/Typography";
import { observer } from "mobx-react-lite";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { APIRequestErrorInterface } from "../../api/fetchApi";
import { useApiCall } from "../../hooks/useApiCall";
import i18nStore from "../../i18n/store";
import getFromNow from "../../utils/dateutils";
import { getProjectBranches } from "../tables/api/branches";
import { getProjectNodes } from "../tables/api/nodes";
import { BranchInterface } from "../tables/schemas/branches";
import { NodeInterface } from "../tables/schemas/nodes";
import { SCCCalcApi } from "./api";
import ChartList from "./components/charts/ChartList";
import CalcResultFilters from "./components/filters/CalcResultFilters";
import filtersStore from "./components/filters/filtersStore";
import filterStore from "./components/filters/filtersStore";
import ResultBranchesTable from "./components/tables/ResultBranchesTable";
import ResultDamageParamsTable from "./components/tables/ResultDamageParamsTable";
import ResultNodesTable from "./components/tables/ResultNodesTable";
import { CalcBranchInterface, CalcNodeInterface } from "./schemas";


export const CalcPageTabs = {
    Nodes: 1,
    NodesCharts: 2,
    Branches: 3,
    BranchesCharts: 4,
    DefectParams: 5,
};

// TODO: Блокировать повторный расчет


const Item = styled(Paper)(({theme}) => ({
    backgroundColor: '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    ...theme.applyStyles('dark', {
        backgroundColor: '#1A2027',
    }),
}));


const CalculationsPage = observer(() => {
    const { enqueueSnackbar } = useSnackbar();
    const translations = i18nStore.getTranslations;
    const params = useParams();
    const theme = useTheme();
    const projectId: number = +(params.projectId || 0);

    const getResultNodesApi = useApiCall(() => SCCCalcApi.getResultNodes(projectId), []);
    const getResultBranchesApi = useApiCall(() => SCCCalcApi.getResultBranches(projectId), []);
    const getResultDamageParamsApi = useApiCall(() => SCCCalcApi.getResultDamageParams(projectId), []);
    const getLastResultInfoApi = useApiCall(() => SCCCalcApi.getLastResultInfo(projectId), null);
    const getNodesApi = useApiCall<NodeInterface[]>(() => getProjectNodes(projectId), []);
    const getBranchesApi = useApiCall<BranchInterface[]>(() => getProjectBranches(projectId), []);

    const [nodeNameSetFlag, setNodeNameSetFlag] = useState<boolean>(false);
    const [branchNameSetFlag, setBranchNameSetFlag] = useState<boolean>(false);

    useEffect(() => {
        getInitData();
    }, []);

    const getInitData = () => {
        getLastResultInfoApi.apiCaller();
        getNodesApi.apiCaller()?.then(nodes => {
            // @ts-ignore
            filterStore._setNodes(nodes);
            return nodes;
        });
        getBranchesApi.apiCaller();
    }

    const getTabData = (tab: number) => {
        if (tab === CalcPageTabs.Nodes) {
            getResultNodesApi.setData([]);
            setNodeNameSetFlag(false);
            getResultNodesApi.apiCaller()?.then(() => {});
        } else if (tab === CalcPageTabs.Branches) {
            getResultBranchesApi.setData([]);
            getResultBranchesApi.apiCaller()?.then(() => {
                setBranchNameSetFlag(false);
            });
        } else if (tab === CalcPageTabs.DefectParams) {
            getResultDamageParamsApi.setData([]);
            getResultDamageParamsApi.apiCaller();
        } else if (tab === CalcPageTabs.NodesCharts) {
            setNodeNameSetFlag(false);
            getResultNodesApi.apiCaller();
        } else if (tab === CalcPageTabs.BranchesCharts) {
            getResultBranchesApi.apiCaller();
        }
    };

    useEffect(() => {
        getTabData(filterStore.getTab);
    }, [filterStore.getTab]);

    useEffect(() => {
        // При обновлении расчетных данных узлов - дополнить названиями
        if (!nodeNameSetFlag && getResultNodesApi.data.length > 0 && getNodesApi.data.length > 0) {
            let nodeId2Name = new Map();
            for (const node of getNodesApi.data) {
                nodeId2Name.set(node.id, node.name);
            }

            getResultNodesApi.setData((resultNodes: CalcNodeInterface[]) => resultNodes.map(
                (resultNode: CalcNodeInterface) => {
                    return {...resultNode, name: nodeId2Name.get(resultNode.node_pk)};
                }
            ));
            setNodeNameSetFlag(true);
        }
    }, [getResultNodesApi.data, getNodesApi.data, nodeNameSetFlag]);

    useEffect(() => {
        // При обновлении расчетных данных узлов - дополнить названиями
        if (!branchNameSetFlag) {
            let branchId2Name = new Map();
            for (const branch of getBranchesApi.data) {
                branchId2Name.set(branch.id, branch.name);
            }

            getResultBranchesApi.setData((resultBranches: CalcBranchInterface[]) => resultBranches.map(
                (resultBranch: CalcBranchInterface) => {
                    resultBranch.name = branchId2Name.get(resultBranch.branch_pk) || "-";
                    return resultBranch;
                }
            ));
            setBranchNameSetFlag(true);
        }
    }, [getResultBranchesApi.data, getBranchesApi.data, branchNameSetFlag]);

    const makeCalcApi = useApiCall(
        () => SCCCalcApi.makeCalc(projectId)
            .then((result) => {
                getTabData(filterStore.getTab);
                return result;
            })
            .catch((err: APIRequestErrorInterface) => {
                enqueueSnackbar(err.details.detail, {variant: "error"});
                return err;
            }),
        {},
    );

    const makeCalculations = () => {
        makeCalcApi.apiCaller()?.then(() => {
            getInitData();
            if (filterStore.getProjectId !== projectId || (!filtersStore.getSelectedNode && getNodesApi.data.length > 0)) {
                filterStore.setProjectId(projectId);
                filterStore.setSelectedNode(getNodesApi.data[0].id);
            }
        });
    };

    return (
        <Box>
            <Box sx={{display: "flex", alignItems: "center", width: "100%"}}>
                <Button
                    variant="contained"
                    sx={{minWidth: 180, mr: 3}}
                    onClick={() => makeCalculations()}
                    loading={makeCalcApi.loading}
                    disabled={getNodesApi.loading || getBranchesApi.loading || getResultNodesApi.loading || getResultBranchesApi.loading || getResultDamageParamsApi.loading}
                >
                    {translations.calculate}
                </Button>

                {
                    Boolean(getLastResultInfoApi.data) && (
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            <Box sx={{fontSize: "0.9rem", color: theme.palette.grey.A700, mr: 1, flexGrow: 1,}}>
                                {translations._calc.lastCalculation}: {getFromNow(getLastResultInfoApi.data?.finished_at)} {translations.ago},
                            </Box>
                            <Typography variant="subtitle2" sx={{fontSize: "0.9rem", textDecoration: "underline"}}>
                                {getLastResultInfoApi.data?.user.name}
                            </Typography>
                        </Box>
                    )
                }

            </Box>

            <Grid container spacing={0}>
                <Grid size="grow">
                    <Item elevation={0}>
                        <TabContext value={filterStore.getTab}>
                            <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                                <TabList
                                    onChange={(event: React.SyntheticEvent, newValue: number) => filterStore.setTab(newValue)}>
                                    <Tab
                                        label={translations.nodes}
                                        value={CalcPageTabs.Nodes}
                                        sx={{fontSize: '0.8rem', padding: '0 16px'}}
                                    />
                                    <Tab
                                        label={
                                            <Box sx={{display: 'flex', alignItems: "center"}}>
                                                <Box sx={{mr: 1}}>{translations.nodes}</Box>
                                                <TrendingUpIcon/>
                                            </Box>
                                        }
                                        value={CalcPageTabs.NodesCharts}
                                        sx={{fontSize: '0.8rem', padding: '0 16px'}}
                                    />
                                    <Tab
                                        label={translations.branches}
                                        value={CalcPageTabs.Branches}
                                        sx={{fontSize: '0.8rem', padding: '0 16px'}}
                                    />
                                    <Tab
                                        label={
                                            <Box sx={{display: 'flex', alignItems: "center"}}>
                                                <Box sx={{mr: 1}}>{translations.branches}</Box>
                                                <TrendingUpIcon />
                                            </Box>
                                        }
                                        value={CalcPageTabs.BranchesCharts}
                                        sx={{fontSize: '0.8rem', padding: '0 16px'}}
                                    />
                                    <Tab
                                        label={translations.damageParams}
                                        value={CalcPageTabs.DefectParams}
                                        sx={{fontSize: '0.8rem', padding: '0 16px'}}
                                    />
                                </TabList>
                            </Box>

                            <TabPanel value={CalcPageTabs.Nodes} sx={{ px: 0}}>
                                <Box sx={{ display: "flex" }}>
                                    <Box>
                                        <CalcResultFilters
                                            nodes={getNodesApi.data}
                                        />
                                    </Box>
                                    <ResultNodesTable
                                        rows={getResultNodesApi.data}
                                        loading={getResultNodesApi.loading}
                                    />
                                </Box>
                            </TabPanel>
                            <TabPanel value={CalcPageTabs.NodesCharts} sx={{px: 0}}>
                                <ChartList
                                    nodes={getNodesApi.data}
                                    calcNodes={getResultNodesApi.data}
                                />
                            </TabPanel>
                            <TabPanel value={CalcPageTabs.Branches} sx={{px: 0}}>
                                <Box sx={{ display: "flex" }}>
                                    <CalcResultFilters
                                        nodes={getNodesApi.data}
                                    />
                                    <ResultBranchesTable
                                        rows={getResultBranchesApi.data}
                                        loading={getResultBranchesApi.loading}
                                    />
                                </Box>
                            </TabPanel>
                            {/*<TabPanel value={CalcPageTabs.BranchesCharts} sx={{px: 0}}>*/}

                            {/*</TabPanel>*/}
                            <TabPanel value={CalcPageTabs.DefectParams} sx={{px: 0}}>
                                <Box sx={{ display: "flex" }}>
                                    <CalcResultFilters
                                        nodes={getNodesApi.data}
                                    />
                                    <ResultDamageParamsTable
                                        rows={getResultDamageParamsApi.data}
                                        loading={getResultDamageParamsApi.loading}
                                    />
                                </Box>
                            </TabPanel>
                        </TabContext>
                    </Item>
                </Grid>
            </Grid>
        </Box>
    );
});


export default CalculationsPage;
